<template>
  <div id="satellitePanel">
    <!-- Map-overlay tile-loading indicator. Positioned over the visible map
         area (right of the drawer) so farmers see activity while rasters
         stream in, even if they're not looking at the drawer itself. -->
    <div v-if="tileLoading" class="satellite-map-spinner">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading imagery…</span>
    </div>

    <!-- Full-screen progress overlay while saving datasets -->
    <div v-if="saveProgress.active" class="save-progress-overlay">
      <div class="save-progress-card">
        <div class="save-progress-header">
          <div class="save-progress-title">
            <i class="fas fa-satellite-dish"></i>
            Saving {{ saveProgress.total }} dataset{{ saveProgress.total === 1 ? '' : 's' }}
          </div>
          <div class="save-progress-sub">
            {{ saveProgress.done }} of {{ saveProgress.total }} complete
            <span v-if="saveProgress.failed > 0" class="text-danger ml-2">
              · {{ saveProgress.failed }} failed
            </span>
          </div>
        </div>

        <div class="save-progress-bar-outer">
          <div
            class="save-progress-bar-inner"
            :style="{ width: saveProgress.percent + '%' }"
          ></div>
        </div>

        <div class="save-progress-list">
          <div
            v-for="(item, i) in saveProgress.items"
            :key="i"
            class="save-progress-item"
            :class="'is-' + item.status"
          >
            <span class="save-progress-icon">
              <i v-if="item.status === 'pending'" class="far fa-circle"></i>
              <i v-else-if="item.status === 'running'" class="fas fa-spinner fa-spin"></i>
              <i v-else-if="item.status === 'done'" class="fas fa-check-circle"></i>
              <i v-else-if="item.status === 'failed'" class="fas fa-times-circle"></i>
            </span>
            <span class="save-progress-name">{{ item.name }}</span>
            <span v-if="item.error" class="save-progress-error" :title="item.error">
              <i class="fas fa-triangle-exclamation"></i>
            </span>
          </div>
        </div>

        <div v-if="saveProgress.done === saveProgress.total" class="save-progress-footer">
          <button
            type="button"
            class="btn btn-primary btn-block"
            @click="dismissSaveProgress"
          >
            <i class="fas fa-check"></i> Done
          </button>
        </div>
      </div>
    </div>

    <b-card
      class="blockDrawer large"
      :class="$store.state.drawer.satellite.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="closePanel"
        >
          <i class="far fa-times"></i>
        </a>
        <h4 class="card-title">
          <i class="fas fa-satellite"></i> Satellite Imagery
        </h4>
        <small class="text-muted">
          Browse and ingest NASA &amp; ESA open-data imagery for your farm blocks.
        </small>
      </template>

      <!-- No block selected -->
      <div v-if="!selectedBlock" class="alert alert-info mb-3">
        <strong>Select a block</strong> from the list below, or tap any block polygon on the map.
      </div>

      <!-- Current block card (when selected). In multi-select mode the
           subtitle and badge swap to a "N blocks" summary so the user
           always knows what the next satellite operation will operate on. -->
      <div v-if="selectedBlock" class="selected-block mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <small class="text-muted d-block">
              {{ multiSelectMode && effectiveBlockCount > 1 ? 'Active blocks' : 'Active block' }}
            </small>
            <strong v-if="!multiSelectMode || effectiveBlockCount === 1">
              {{ selectedBlock.name || selectedBlock.id }}
            </strong>
            <strong v-else>
              {{ selectedBlock.name || selectedBlock.id }}
              <small class="text-muted ml-1">+ {{ effectiveBlockCount - 1 }} more</small>
            </strong>
          </div>
          <span
            v-if="!multiSelectMode && selectedBlock.area"
            class="badge badge-light"
          >{{ formatArea(selectedBlock.area) }}</span>
          <span
            v-else-if="multiSelectMode && totalSelectedArea > 0"
            class="badge badge-light"
          >{{ formatArea(totalSelectedArea) }} total</span>
        </div>
      </div>

      <!-- Tab switcher -->
      <div v-if="selectedBlock" class="sat-tabs">
        <button
          type="button"
          class="sat-tab"
          :class="{ 'is-active': activeTab === 'browse' }"
          @click="activeTab = 'browse'"
        >
          <i class="fas fa-search"></i> Browse scenes
        </button>
        <button
          type="button"
          class="sat-tab"
          :class="{ 'is-active': activeTab === 'analyses' }"
          @click="activeTab = 'analyses'"
        >
          <i class="fas fa-flask"></i> Analyses
        </button>
        <!-- AVIRIS-3 tab only appears for farms with features.aviris set
             (toggled per-farm from the admin Farm Management panel). -->
        <button
          v-if="avirisEnabled"
          type="button"
          class="sat-tab"
          :class="{ 'is-active': activeTab === 'aviris' }"
          @click="activeTab = 'aviris'"
        >
          <i class="fas fa-wave-square"></i> AVIRIS-3
        </button>
      </div>

      <!-- Browse tab -->
      <div v-if="activeTab === 'browse' && selectedBlock">
        <b-btn
          variant="primary"
          block
          :disabled="loadingScenes"
          @click="openSceneModal"
        >
          <i class="fas fa-search"></i>
          {{ availableScenes.length ? 'Browse another scene' : 'Browse Imagery' }}
        </b-btn>

        <!-- Currently loaded scene -->
        <div
          v-if="currentLoadedScene"
          class="loaded-scene mt-3"
        >
          <div class="loaded-scene-head">
            <span class="scene-icon">{{ currentLoadedScene.sourceIcon || '🛰️' }}</span>
            <div class="flex-grow-1">
              <strong class="d-block">{{ currentLoadedScene.source || 'Scene' }}</strong>
              <small class="text-muted">{{ sceneDateDisplay(currentLoadedScene) }}</small>
            </div>
            <div
              v-if="tileLoading"
              class="tile-loading-inline"
              title="Loading tiles…"
            >
              <i class="fas fa-spinner fa-spin"></i>
            </div>
          </div>

          <b-btn
            variant="success"
            block
            class="mt-2"
            :disabled="!satelliteLayerAdded || tileLoading"
            @click="saveCurrentSceneAsDataset"
          >
            <i class="fas fa-database"></i>
            {{ tileLoading ? 'Loading tiles…' : 'Save as Dataset' }}
          </b-btn>

          <b-btn
            variant="outline-secondary"
            block
            size="sm"
            class="mt-1"
            @click="clearLoadedScene"
          >
            <i class="fas fa-times"></i> Clear overlay
          </b-btn>
        </div>
      </div>

      <!-- Analyses tab -->
      <AnalysesTab
        v-if="activeTab === 'analyses' && selectedBlock"
        :selectedBlock="selectedBlock"
        :multiSelectActive="multiSelectMode"
        :extraBlockCount="effectiveBlockCount - 1"
        @analysisStart="onAnalysisStart"
        @analysisComplete="onAnalysisComplete"
        @analysisError="onAnalysisError"
      />

      <!-- AVIRIS-3 hyperspectral tab — gated on the farm's features.aviris -->
      <Aviris3Tab
        v-if="activeTab === 'aviris' && avirisEnabled && selectedBlock"
        :selectedBlock="selectedBlock"
      />

      <!-- Block list — browse and jump to any block. Toggling multi-
           select mode shows checkboxes and Select-all / Clear actions
           so the user can run satellite operations across many blocks
           (or every block on the farm) at once. -->
      <div class="block-list-section mt-3">
        <h6 class="block-list-header">
          <i class="fas fa-map-marked-alt"></i>
          Blocks
          <span class="block-count">{{ blocks.length }}</span>
          <button
            type="button"
            class="multi-toggle"
            :class="{ 'is-on': multiSelectMode }"
            @click="toggleMultiSelectMode"
            :title="multiSelectMode ? 'Switch back to single-block mode' : 'Select multiple blocks at once'"
          >
            <i :class="multiSelectMode ? 'fas fa-check-square' : 'far fa-square'"></i>
            {{ multiSelectMode ? 'Multi-select on' : 'Multi-select' }}
          </button>
        </h6>

        <!-- Bulk actions visible only when multi-select is on -->
        <div v-if="multiSelectMode" class="multi-actions">
          <button type="button" class="btn-link" @click="selectAllBlocks">
            <i class="fas fa-check-double"></i> Select all
          </button>
          <button
            type="button"
            class="btn-link"
            :disabled="effectiveBlockCount <= 1"
            @click="clearExtraBlocks"
          >
            <i class="fas fa-times"></i> Clear extra
          </button>
          <span class="multi-summary">{{ effectiveBlockCount }} block{{ effectiveBlockCount === 1 ? '' : 's' }} selected</span>
        </div>

        <div class="block-list">
          <a
            v-for="block in blocks"
            :key="block.id"
            class="block-list-item clickable"
            :class="{
              'is-active': selectedBlock && selectedBlock.id === block.id,
              'is-extra': multiSelectMode && extraBlockIds.includes(block.id)
            }"
            @click="onBlockRowClick(block)"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div class="block-list-name">
                <!-- Multi-select checkbox marker — visual only; the
                     whole row toggles selection via onBlockRowClick. -->
                <i
                  v-if="multiSelectMode"
                  class="multi-check mr-2"
                  :class="isBlockChecked(block) ? 'fas fa-check-square' : 'far fa-square'"
                ></i>
                <strong>{{ block.name || block.id }}</strong>
              </div>
              <small
                v-if="block.area"
                class="text-muted"
              >{{ formatArea(block.area) }}</small>
            </div>
          </a>
          <div v-if="!blocks.length" class="text-muted small text-center py-3">
            No blocks on this farm yet. Create blocks from the farm menu first.
          </div>
        </div>
      </div>

      <!-- Sponsor footer -->
      <div class="sponsor-footer mt-4 text-center">
        <a
          href="https://nasaacres.org"
          target="_blank"
          rel="noopener"
        >
          <img
            v-if="nasaAcresLogo"
            :src="nasaAcresLogo"
            alt="NASA Acres"
            class="nasa-acres-logo-img"
          />
          <div class="nasa-acres-caption">Supported by NASA Acres</div>
        </a>
      </div>
    </b-card>

    <!-- Scene Selection Modal (reuse from plugin) -->
    <SceneSelectionModal
      :show="showSceneModal"
      :scenes="availableScenes"
      :loading="loadingScenes"
      :catalog="collectionCatalog"
      :initialCollections="activeCollections"
      :emptyCollections="emptyCollections"
      @close="closeSceneModal"
      @select="selectScene"
      @search="handleDateRangeSearch"
      @loadLatestForCollection="loadLatestForCollection"
    />

    <!-- Dataset Preview Modal (reuse from plugin) -->
    <DatasetPreviewModal
      v-if="showDatasetModal"
      :show="showDatasetModal"
      :scene="currentLoadedScene"
      :block="selectedBlock"
      :multiBlockCount="effectiveBlockCount"
      :multiBlockNames="effectiveBlocks.map(b => b.name)"
      @close="closeDatasetModal"
      @create="createMultipleDatasets"
    />
  </div>
</template>

<script>
import SceneSelectionModal from './components/SceneSelectionModal.vue'
import DatasetPreviewModal from './components/DatasetPreviewModal.vue'
import AnalysesTab from './components/AnalysesTab.vue'
import Aviris3Tab from './components/Aviris3Tab.vue'
import SatelliteImageryService from './services/SatelliteImageryService.js'
const turf = require('@turf/turf')
const fb = require('@/firebaseConfig.js')

let nasaAcresLogo = null
try {
  // eslint-disable-next-line global-require
  nasaAcresLogo = require('@/assets/nasa-acres.png')
} catch (e) {
  nasaAcresLogo = null
}

const SAT_SOURCE_ID = 'ev-satellite-scene'
const SAT_LAYER_ID = 'ev-satellite-scene-layer'

const TITILER_BASE = 'https://titiler-231171307256.us-central1.run.app'

export default {
  name: 'SatellitePanel',
  components: { SceneSelectionModal, DatasetPreviewModal, AnalysesTab, Aviris3Tab },
  data () {
    return {
      satelliteService: new SatelliteImageryService(),
      nasaAcresLogo,
      activeTab: 'browse',
      showSceneModal: false,
      showDatasetModal: false,
      loadingScenes: false,
      availableScenes: [],
      emptyCollections: [],
      currentLoadedScene: null,
      satelliteLayerAdded: false,
      tileLoading: false,
      saveProgress: {
        active: false,
        items: [],   // [{ name, status: 'pending'|'running'|'done'|'failed', error }]
        total: 0,
        done: 0,
        failed: 0,
        percent: 0
      },
      collectionCatalog: [],
      activeCollections: [],
      dateRange: null,
      // Multi-block selection — opt-in toggle so the default flow stays
      // single-block (the existing behavior every doc + tutorial assumes).
      // When multiSelectMode is on, the block list shows checkboxes and
      // every downstream operation (browse, save, analyses) fans out
      // across `extraBlockIds` ∪ the canonical selected block.
      multiSelectMode: false,
      extraBlockIds: []  // array of block ids in addition to selectedBlock
    }
  },
  computed: {
    selectedBlock () {
      return this.$store.state.block && this.$store.state.block.id ? this.$store.state.block : null
    },
    /**
     * Effective list of blocks the next satellite operation should run
     * against. In single-select mode (default) this is just the
     * canonical selectedBlock as a single-element array. In multi-select
     * mode it's the union of selectedBlock and every block id in
     * extraBlockIds, looked up against the GeoJSON features cache so
     * each entry has geometry attached.
     *
     * Always returns an array — call sites should iterate even when
     * there's only one element, which keeps the multi/single code paths
     * identical.
     */
    effectiveBlocks () {
      const primary = this.selectedBlock
      // Single-select mode (or no extras) — just the canonical block doc.
      // Its `footprint` is already a JSON string as the satellite backends
      // expect (see satelliteProcessor.js's JSON.parse(block.footprint)).
      if (!this.multiSelectMode || !this.extraBlockIds.length) {
        return primary ? [primary] : []
      }
      // Multi-select. Synthesize the same shape for extra blocks pulled
      // from the GeoJSON cache (we don't load every Firestore block
      // doc just to send a search), stringifying footprint to match the
      // canonical doc's contract.
      const features = (this.$store.state.blocksGeoJson && this.$store.state.blocksGeoJson.features) || []
      const byId = new Map(features.map(f => [f.properties.blockId, f]))
      const out = primary ? [primary] : []
      const primaryId = primary && primary.id
      for (const id of this.extraBlockIds) {
        if (id === primaryId) continue
        const f = byId.get(id)
        if (!f) continue
        out.push({
          id,
          name: f.properties.name || id,
          area: f.properties.area || null,
          footprint: JSON.stringify({ type: 'FeatureCollection', features: [f] }),
          // Convenience handle for effectiveGeometry below — not read by
          // any backend, just by this component.
          _geometry: f.geometry
        })
      }
      return out
    },
    /**
     * Synthetic geometry covering every selected block. For multi-block
     * scene search we want STAC to consider a bbox that wraps all of
     * them at once. calculateBoundingBox() in the satellite service
     * already handles MultiPolygon, so we wrap the per-block polygons
     * into one without further math.
     *
     * Returns null when nothing is selected.
     */
    effectiveGeometry () {
      // Helper — extract a geometry from either the canonical Firestore
      // block doc (footprint as JSON string) or a synthetic extra
      // entry (carries _geometry directly).
      const toGeom = (b) => {
        if (!b) return null
        if (b._geometry) return b._geometry
        if (!b.footprint) return null
        try {
          const fp = typeof b.footprint === 'string' ? JSON.parse(b.footprint) : b.footprint
          return fp?.features?.[0]?.geometry || fp?.geometry || null
        } catch (e) { return null }
      }
      const polys = this.effectiveBlocks.map(toGeom).filter(Boolean)
      if (!polys.length) return null
      if (polys.length === 1) return polys[0]
      // Wrap each polygon's coordinates into a MultiPolygon. Handles
      // mixed Polygon / MultiPolygon inputs.
      const multi = { type: 'MultiPolygon', coordinates: [] }
      polys.forEach(g => {
        if (g.type === 'Polygon') multi.coordinates.push(g.coordinates)
        else if (g.type === 'MultiPolygon') g.coordinates.forEach(c => multi.coordinates.push(c))
      })
      return multi
    },
    /**
     * How many blocks the operation will touch. Drives the UI summary
     * line and the "this will create N datasets" confirmation copy.
     */
    effectiveBlockCount () {
      return this.effectiveBlocks.length
    },
    /**
     * Sum of acres / hectares across every selected block. Surfaced
     * in the active-block badge so the user has a sense of total area
     * the upcoming operation will cover.
     */
    totalSelectedArea () {
      return this.effectiveBlocks.reduce((sum, b) => sum + (Number(b.area) || 0), 0)
    },
    map () {
      return this.$store.state.mapObject
    },
    /**
     * Whether this farm has AVIRIS-3 hyperspectral access. Toggled
     * per-farm from the admin Farm Management panel (features.aviris).
     * Gates the AVIRIS-3 tab — false for every farm that hasn't been
     * granted access.
     */
    avirisEnabled () {
      const farm = this.$store.state.farm
      return !!(farm && farm.features && farm.features.aviris)
    },
    blocks () {
      // Prefer the full GeoJSON features so we have geometry for zooming.
      const features = (this.$store.state.blocksGeoJson &&
        this.$store.state.blocksGeoJson.features) || []
      const out = features.map(f => ({
        id: f.properties.blockId,
        name: f.properties.name || f.properties.blockName || f.properties.blockId,
        area: f.properties.area,
        geometry: f.geometry
      }))
      // Sort by name (case-insensitive)
      out.sort((a, b) => String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase()))
      return out
    }
  },
  mounted () {
    this.collectionCatalog = this.satelliteService.getCollectionCatalog()
    this.activeCollections = [...this.satelliteService.defaultCollections]
    const end = new Date()
    const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
    this.dateRange = { startDate: start, endDate: end }
  },
  beforeDestroy () {
    // Auto-clear the satellite overlay when the drawer closes. Keeping a
    // stale raster around after the panel closes confuses the farmer — they
    // can't remove it (the "Clear overlay" button is only in the drawer) and
    // they don't see metadata about what it is.
    this.clearSatelliteLayer()
    // Same reasoning for the multi-block selection highlight — leaving
    // a yellow union outline on the map after the drawer is gone would
    // be a "what does that mean and how do I remove it?" question.
    try {
      const m = this.$store.state.mapObject
      if (m) {
        if (m.getLayer('satelliteMultiBlockHighlight')) m.removeLayer('satelliteMultiBlockHighlight')
        if (m.getSource('satelliteMultiBlockHighlightSrc')) m.removeSource('satelliteMultiBlockHighlightSrc')
      }
    } catch (e) { /* map may already be torn down */ }
  },
  methods: {
    closePanel () {
      this.$store.commit('hideDrawer', 'satellite')
    },

    /**
     * Select a block from the panel's block list: zoom the main map to its
     * bounds, load the full block document into $store.state.block (matching
     * the pattern used by the on-map block-click handler), and clear any
     * stale overlays from a previous scene on another block.
     */
    /**
     * Refresh (or clear) the map's multi-block highlight + optionally
     * fitBounds to wrap every selected block. Mirrors what the
     * single-block selectBlockFromList does for one block, but the
     * highlight layer is a separate id (`satelliteMultiBlockHighlight`)
     * so it stacks cleanly with the existing single-block
     * `blockHighlightLayer` and tears down independently.
     *
     * @param {Object} [opts]
     * @param {Boolean} [opts.fit=true]   Whether to fitBounds to the
     *                                    union. Pass false when the call
     *                                    is reactive (e.g. mode-toggle
     *                                    cleanup) rather than user-
     *                                    driven.
     */
    refreshMultiBlockHighlight (opts) {
      const m = this.map
      if (!m) return
      const fit = !opts || opts.fit !== false
      const layerId = 'satelliteMultiBlockHighlight'
      const srcId = 'satelliteMultiBlockHighlightSrc'

      // Helper — tear the highlight down cleanly.
      const teardown = () => {
        if (m.getLayer(layerId)) m.removeLayer(layerId)
        if (m.getSource(srcId)) m.removeSource(srcId)
      }

      // Pull a geometry off each effective block — the canonical
      // Firestore primary stores `footprint` as a JSON string, the
      // synthetic extras carry `_geometry` directly.
      const toGeom = (b) => {
        if (!b) return null
        if (b._geometry) return b._geometry
        if (!b.footprint) return null
        try {
          const fp = typeof b.footprint === 'string' ? JSON.parse(b.footprint) : b.footprint
          return fp?.features?.[0]?.geometry || fp?.geometry || null
        } catch (e) { return null }
      }

      // Off-mode or single-block — nothing to draw beyond the existing
      // blockHighlightLayer the single-select path already manages.
      if (!this.multiSelectMode || this.effectiveBlockCount <= 1) {
        teardown()
        return
      }

      const features = this.effectiveBlocks
        .map(b => {
          const g = toGeom(b)
          if (!g) return null
          return { type: 'Feature', geometry: g, properties: { blockId: b.id, blockName: b.name } }
        })
        .filter(Boolean)
      if (!features.length) {
        teardown()
        return
      }

      const fc = { type: 'FeatureCollection', features }
      if (m.getSource(srcId)) {
        m.getSource(srcId).setData(fc)
      } else {
        m.addSource(srcId, { type: 'geojson', data: fc })
        m.addLayer({
          id: layerId,
          type: 'line',
          source: srcId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ffc107',  // matches the single-block highlight
            'line-width': 4,
            'line-opacity': 0.95
          }
        })
      }

      if (fit) {
        // Fit the union of every block's bbox. turf is already in scope
        // (used elsewhere for single-block fitBounds).
        try {
          const bbox = turf.bbox(fc)
          m.fitBounds(bbox, { padding: 80, animate: true, duration: 800, maxZoom: 17 })
        } catch (err) {
          console.warn('Could not fit multi-block bounds:', err)
        }
      }
    },
    /**
     * Whether the given block is part of the active selection set
     * (canonical selectedBlock OR an extra block in multi mode).
     */
    isBlockChecked (block) {
      if (!block || !block.id) return false
      if (this.selectedBlock && this.selectedBlock.id === block.id) return true
      return this.multiSelectMode && this.extraBlockIds.includes(block.id)
    },
    /**
     * Single click on a block row. In single-select mode it behaves
     * exactly like the old selectBlockFromList. In multi-select mode it
     * toggles the block's membership in extraBlockIds (unless it's the
     * canonical selectedBlock, which can't be toggled off here — pick a
     * different block first or turn off multi-select to swap it).
     */
    onBlockRowClick (block) {
      if (!this.multiSelectMode) {
        this.selectBlockFromList(block)
        return
      }
      // Multi-select toggle
      if (!block || !block.id) return
      if (this.selectedBlock && this.selectedBlock.id === block.id) {
        // The "primary" block is always selected. To deselect it the
        // user would need to pick a different block as the primary.
        // Silently no-op — better than a confusing surprise.
        return
      }
      const idx = this.extraBlockIds.indexOf(block.id)
      if (idx === -1) {
        this.extraBlockIds = [...this.extraBlockIds, block.id]
      } else {
        this.extraBlockIds = this.extraBlockIds.filter(id => id !== block.id)
      }
      // Clear scene overlay since the bbox may have changed materially.
      this.clearLoadedScene()
      this.availableScenes = []
      this.emptyCollections = []
      // Redraw highlights + zoom to fit the (new) union. Wait one tick
      // so the reactive computed (effectiveBlocks) sees the updated
      // extraBlockIds before refresh reads it.
      this.$nextTick(() => this.refreshMultiBlockHighlight({ fit: true }))
    },
    toggleMultiSelectMode () {
      this.multiSelectMode = !this.multiSelectMode
      if (!this.multiSelectMode) {
        // Switching back to single mode resets the extra list so a
        // future toggle-on doesn't surprise the user with stale picks.
        this.extraBlockIds = []
      }
      this.clearLoadedScene()
      this.availableScenes = []
      this.emptyCollections = []
      // Tear down the multi-highlight when turning OFF. Turning ON
      // with no extras yet is a no-op until the user picks one.
      this.$nextTick(() => this.refreshMultiBlockHighlight({ fit: false }))
    },
    selectAllBlocks () {
      if (!this.multiSelectMode) return
      const primaryId = this.selectedBlock ? this.selectedBlock.id : null
      this.extraBlockIds = this.blocks
        .map(b => b.id)
        .filter(id => id && id !== primaryId)
      this.clearLoadedScene()
      this.availableScenes = []
      this.emptyCollections = []
      this.$nextTick(() => this.refreshMultiBlockHighlight({ fit: true }))
    },
    clearExtraBlocks () {
      this.extraBlockIds = []
      this.clearLoadedScene()
      this.availableScenes = []
      this.emptyCollections = []
      this.$nextTick(() => this.refreshMultiBlockHighlight({ fit: false }))
    },
    selectBlockFromList (block) {
      const m = this.map
      if (!m || !block || !block.geometry) return

      // Zoom to the block's bbox
      try {
        const bbox = turf.bbox({ type: 'Feature', geometry: block.geometry, properties: {} })
        m.fitBounds(bbox, { padding: 80, animate: true, duration: 800, maxZoom: 18 })
      } catch (err) {
        console.warn('Could not compute block bbox:', err)
      }

      // If the user switches blocks mid-workflow, clear the current scene overlay
      if (this.selectedBlock && this.selectedBlock.id !== block.id) {
        this.clearLoadedScene()
        this.availableScenes = []
        this.emptyCollections = []
      }

      // Load the full Firestore block doc into store (matches map-click path)
      fb.blocksCollection.doc(block.id).get().then(doc => {
        if (!doc.exists) return
        const data = doc.data()
        data.id = doc.id
        this.$store.state.block = data
        // Highlight on the map — reuse the same pattern the map-click uses
        try {
          if (m.getLayer('blockHighlightLayer')) m.removeLayer('blockHighlightLayer')
          if (m.getSource('blockHighlightLayer')) m.removeSource('blockHighlightLayer')
          // Draw a border, not a fill — keeps underlying imagery visible.
          m.addLayer({
            id: 'blockHighlightLayer',
            type: 'line',
            source: {
              type: 'geojson',
              data: { type: 'Feature', geometry: block.geometry, properties: {} }
            },
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#ffc107',
              'line-width': 4,
              'line-opacity': 0.95
            }
          })
        } catch (err) {
          console.warn('Could not highlight block:', err)
        }
        // If multi-select is on, keep the union highlight in sync with
        // the new primary. fit:false here — selectBlockFromList already
        // zoomed to the primary above, and refitting to the union now
        // would feel like a second jump.
        if (this.multiSelectMode) {
          this.$nextTick(() => this.refreshMultiBlockHighlight({ fit: false }))
        }
      }).catch(err => {
        console.error('Failed to load block doc:', err.message)
      })
    },

    formatArea (area) {
      if (!area) return ''
      if (area > 1000) return `${(area * 0.000247105).toFixed(2)} ac`
      return `${area.toFixed(1)} m²`
    },

    sceneDateDisplay (scene) {
      const p = scene && scene.properties ? scene.properties : {}
      const fmt = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      if (p.datetime) return fmt(p.datetime)
      if (scene.datetime) return fmt(scene.datetime)
      if (p.start_datetime && p.end_datetime) return `${fmt(p.start_datetime)} → ${fmt(p.end_datetime)}`
      if (p.start_datetime) return fmt(p.start_datetime)
      if (p.end_datetime) return fmt(p.end_datetime)
      return 'Unknown date'
    },

    // ---------------- search flow ----------------

    openSceneModal () {
      if (!this.selectedBlock) return
      this.showSceneModal = true
      if (this.availableScenes.length === 0) {
        this.performSatelliteSearch()
      }
    },

    closeSceneModal () {
      this.showSceneModal = false
    },

    async handleDateRangeSearch (payload) {
      this.dateRange = { startDate: payload.startDate, endDate: payload.endDate }
      if (Array.isArray(payload.collections)) this.activeCollections = payload.collections
      await this.performSatelliteSearch()
    },

    async performSatelliteSearch () {
      if (!this.selectedBlock) return
      // In multi-select mode the search bbox covers every selected
      // block at once (MultiPolygon built by effectiveGeometry); in
      // single-select it's just the active block's polygon. Either
      // way the STAC search returns scenes that overlap the union.
      const searchGeom = this.effectiveGeometry
      if (!searchGeom) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'Block geometry not found.',
          level: 'alert-warning',
          timeout: 4000
        })
        return
      }
      this.loadingScenes = true
      try {
        const imagery = await this.satelliteService.searchLatestImagery(
          searchGeom,
          20,
          this.dateRange,
          this.activeCollections && this.activeCollections.length ? this.activeCollections : null
        )
        this.availableScenes = (imagery && imagery.features) || []
        this.emptyCollections = (imagery && imagery.emptyCollections) || []
      } catch (err) {
        console.error('Satellite search failed:', err)
        this.availableScenes = []
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error searching satellite imagery.',
          level: 'alert-danger',
          timeout: 5000
        })
      } finally {
        this.loadingScenes = false
      }
    },

    async loadLatestForCollection (collectionId) {
      if (!this.selectedBlock) return
      const blockFeature = (this.$store.state.blocksGeoJson?.features || [])
        .find(f => f.properties.blockId === this.selectedBlock.id)
      if (!blockFeature) return
      this.loadingScenes = true
      try {
        const resp = await this.satelliteService.searchLatestForCollection(collectionId, blockFeature.geometry, 8)
        const added = (resp && resp.features) || []
        if (added.length === 0) {
          this.$store.commit('setAlert', {
            active: true,
            message: `No ${collectionId} scenes found for this block in the entire archive.`,
            level: 'alert-warning',
            timeout: 5000
          })
          return
        }
        const pinned = added.map(s => ({ ...s, __loadedLatest: true }))
        const existing = new Set(pinned.map(s => s.id))
        const rest = this.availableScenes.filter(s => !existing.has(s.id))
        this.availableScenes = [...pinned, ...rest]
        this.emptyCollections = this.emptyCollections.filter(c => c !== collectionId)
      } catch (err) {
        console.error('Load latest failed:', err)
      } finally {
        this.loadingScenes = false
      }
    },

    // ---------------- scene → main map ----------------

    selectScene (scene) {
      this.addSceneToMainMap(scene)
      this.closeSceneModal()
    },

    clearSatelliteLayer () {
      const m = this.map
      if (!m) return
      try {
        if (m.getLayer(SAT_LAYER_ID)) m.removeLayer(SAT_LAYER_ID)
        if (m.getSource(SAT_SOURCE_ID)) m.removeSource(SAT_SOURCE_ID)
      } catch (err) {
        console.warn('Error clearing satellite layer:', err)
      }
    },

    /**
     * Remove any dataset-rendered raster (from launchDataset's mapDataset call)
     * that may already be on the map when the user starts browsing satellite
     * scenes. Prevents two overlapping rasters from fighting for the viewport.
     */
    clearPreviousDatasetRaster () {
      const m = this.map
      if (!m) return
      try {
        if (m.getLayer('rasterLayer')) m.removeLayer('rasterLayer')
        if (m.getSource('rasterSource')) m.removeSource('rasterSource')
      } catch (err) {
        console.warn('Error clearing previous dataset raster:', err)
      }
    },

    clearLoadedScene () {
      this.clearSatelliteLayer()
      this.currentLoadedScene = null
      this.satelliteLayerAdded = false
      this.tileLoading = false
    },

    buildMpcRenderParams (collectionId, scene) {
      const assets = Object.keys(scene?.assets || {})
      const has = n => assets.includes(n)
      const encode = v => encodeURIComponent(v)
      if (collectionId === 'landsat-c2-l2') {
        return 'assets=red&assets=green&assets=blue' +
          `&color_formula=${encode('gamma RGB 2.7, saturation 1.5, sigmoidal RGB 15 0.55')}`
      }
      if (collectionId === 'hls2-l30' || collectionId === 'hls2-s30') {
        const r = has('B04') ? 'B04' : 'red'
        const g = has('B03') ? 'B03' : 'green'
        const b = has('B02') ? 'B02' : 'blue'
        return `assets=${r}&assets=${g}&assets=${b}` +
          `&rescale=0,3000&color_formula=${encode('gamma RGB 1.7, sigmoidal RGB 10 0.5')}`
      }
      if (collectionId === 'modis-13Q1-061') {
        const ndviKey = assets.find(a => /NDVI/i.test(a)) || '250m_16_days_NDVI'
        return `assets=${ndviKey}&rescale=-2000,10000&colormap_name=rdylgn`
      }
      if (collectionId === 'naip') return 'assets=image&asset_bidx=image%7C1,2,3'
      if (collectionId === 'io-lulc-9-class') return 'assets=data&colormap_name=io-lulc-9-class'
      if (collectionId === 'esa-worldcover') return 'assets=map&colormap_name=esa-worldcover'
      if (collectionId === '3dep-seamless') return 'assets=data&rescale=-500,4000&colormap_name=terrain'
      if (collectionId === 'gnatsgo-rasters') {
        const soilPreferred = ['aws0_100', 'aws0_150', 'aws0_50', 'aws0_25', 'soc0_30', 'om_r']
        const pick = soilPreferred.find(k => has(k)) || assets.find(a => !/mukey|hzdept/i.test(a)) || assets[0]
        const rescale = /aws/.test(pick) ? '0,400' : /soc|om_r/.test(pick) ? '0,10' : '0,1'
        const cmap = /aws/.test(pick) ? 'blues' : /soc|om_r/.test(pick) ? 'ylorbr' : 'viridis'
        return `assets=${pick}&rescale=${rescale}&colormap_name=${cmap}`
      }
      if (collectionId === 'daymet-daily-na') {
        const pick = ['tmax', 'tmin', 'prcp', 'srad', 'vp', 'swe'].find(k => has(k)) || assets[0]
        const rescale = /tmax|tmin/.test(pick) ? '-30,40'
          : /prcp/.test(pick) ? '0,50'
          : /srad/.test(pick) ? '0,1000' : '0,1'
        const cmap = /tmax|tmin/.test(pick) ? 'rdbu_r' : /prcp/.test(pick) ? 'blues' : 'viridis'
        return `assets=${pick}&rescale=${rescale}&colormap_name=${cmap}`
      }
      if (has('red') && has('green') && has('blue')) {
        return 'assets=red&assets=green&assets=blue' +
          `&color_formula=${encode('gamma RGB 2.5, saturation 1.4, sigmoidal RGB 12 0.5')}`
      }
      return `assets=${assets[0] || 'data'}`
    },

    async addSceneToMainMap (scene) {
      const m = this.map
      if (!m) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'Map not ready yet. Please try again.',
          level: 'alert-warning',
          timeout: 4000
        })
        return
      }

      // Tear down previous overlays — both the last satellite preview AND any
      // dataset raster that was already on the map from a previous launchDataset.
      this.clearSatelliteLayer()
      this.clearPreviousDatasetRaster()
      this.currentLoadedScene = scene
      this.satelliteLayerAdded = false

      const kind = scene.kind || scene.collectionMeta?.kind ||
        (String(scene.collection || '').startsWith('sentinel-1') ? 'sar' : 'optical')
      const previewable = scene.collectionMeta?.previewable !== false

      // Legacy S1 GRD short-circuit
      if (kind === 'sar' && !previewable) {
        this.satelliteLayerAdded = true
        this.$store.commit('setAlert', {
          active: true,
          message: '📡 Legacy Sentinel-1 GRD selected. Use "Save as Dataset" for server-side VV/VH/RVI processing.',
          level: 'alert-info',
          timeout: 6000
        })
        return
      }

      // Build tile URL
      const collectionId = scene.collection || scene.collectionMeta?.id
      const isMpcScene = scene.collectionMeta?.endpoint === 'planetaryComputer' ||
        Object.values(scene.assets || {}).some(a => typeof a?.href === 'string' && /blob\.core\.windows\.net/i.test(a.href))

      let tileUrl = null
      if (kind === 'sar') {
        const vh = scene.assets.vh || scene.assets.vv
        if (vh && vh.href) {
          const expr = encodeURIComponent('10*log10(b1)')
          tileUrl = `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(vh.href)}&expression=${expr}&rescale=-25,0&colormap_name=greys`
        }
      } else if (scene.assets.visual && scene.assets.visual.href) {
        tileUrl = `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(scene.assets.visual.href)}`
      } else if (isMpcScene && collectionId) {
        const renderParams = this.buildMpcRenderParams(collectionId, scene)
        tileUrl = 'https://planetarycomputer.microsoft.com/api/data/v1/item/tiles/' +
          'WebMercatorQuad/{z}/{x}/{y}@1x?' +
          `collection=${encodeURIComponent(collectionId)}` +
          `&item=${encodeURIComponent(scene.id)}` +
          `&${renderParams}`
      } else {
        const fallback = scene.assets.B04 || scene.assets.B02 || scene.assets.red
        if (fallback && fallback.href) {
          tileUrl = `${TITILER_BASE}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(fallback.href)}`
        }
      }

      if (!tileUrl) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No previewable asset on this scene.',
          level: 'alert-warning',
          timeout: 4000
        })
        return
      }

      try {
        this.tileLoading = true
        m.addSource(SAT_SOURCE_ID, {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256
        })
        // Insert below block outlines if present so block polygons stay legible
        const beforeId = m.getLayer('blocksLayerOutline') ? 'blocksLayerOutline' : undefined
        m.addLayer({
          id: SAT_LAYER_ID,
          type: 'raster',
          source: SAT_SOURCE_ID,
          paint: { 'raster-opacity': 0.9 }
        }, beforeId)
        this.satelliteLayerAdded = true

        // Tile-loading detection. We listen for dataloading/data events scoped
        // to our source + poll `areTilesLoaded()` periodically. The polling
        // belt-and-suspenders handles the case where Mapbox fires sourcedata
        // synchronously before our listener is attached.
        const cleanup = () => {
          this.tileLoading = false
          m.off('sourcedata', onSourceData)
          m.off('error', onSourceError)
          if (pollId) clearInterval(pollId)
          if (safetyId) clearTimeout(safetyId)
        }
        const onSourceData = (e) => {
          if (e.sourceId !== SAT_SOURCE_ID) return
          if (e.isSourceLoaded && m.areTilesLoaded && m.areTilesLoaded()) {
            cleanup()
          }
        }
        const onSourceError = (e) => {
          if (e && e.sourceId && e.sourceId !== SAT_SOURCE_ID) return
          cleanup()
        }
        m.on('sourcedata', onSourceData)
        m.on('error', onSourceError)
        // Poll in case the first sourcedata event fired before listener attach
        const pollId = setInterval(() => {
          const src = m.getSource(SAT_SOURCE_ID)
          if (!src) { cleanup(); return }
          if (src.loaded && src.loaded() && m.areTilesLoaded && m.areTilesLoaded()) {
            cleanup()
          }
        }, 400)
        // Safety timeout — never leave the spinner hung
        const safetyId = setTimeout(() => cleanup(), 20000)
      } catch (err) {
        console.error('Failed to add satellite layer to main map:', err)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error loading satellite tiles.',
          level: 'alert-danger',
          timeout: 5000
        })
      }
    },

    // ---------------- save as dataset ----------------

    saveCurrentSceneAsDataset () {
      if (!this.selectedBlock || !this.currentLoadedScene) return
      this.showDatasetModal = true
    },

    closeDatasetModal () {
      this.showDatasetModal = false
    },

    // --- Analysis recipe handlers (Phase 2) ---
    onAnalysisStart (payload) {
      this.$store.commit('setAlert', {
        active: true,
        message: `🧪 Running "${payload.recipe.label}"… this can take 20–90s.`,
        level: 'alert-info',
        timeout: 0
      })
    },
    async onAnalysisComplete (data) {
      this.$store.commit('setAlert', { active: false })
      const meta = data.meta || {}
      this.$store.commit('setAlert', {
        active: true,
        message: `✅ Analysis complete — ${meta.sceneCount || '?'} scenes, ${meta.cellCount || '?'} cells. Opening on map…`,
        level: 'alert-success',
        timeout: 5000
      })
      // Close the panel and launch the resulting dataset on the main map.
      // The backend's HTTP response contains serverTimestamp() sentinels that
      // don't survive JSON encoding — they arrive as plain objects without
      // .toDate(). Re-fetch the doc from Firestore so we get proper
      // Timestamp objects, matching the shape a page refresh would produce.
      try {
        this.clearSatelliteLayer()
        this.currentLoadedScene = null
        this.satelliteLayerAdded = false
        this.$store.commit('hideDrawer', 'satellite')
        let dataset = data.dataset
        if (data.datasetId) {
          try {
            const fb = require('@/firebaseConfig.js')
            const snap = await fb.datasetsCollection.doc(data.datasetId).get()
            if (snap.exists) {
              dataset = snap.data()
              dataset.id = snap.id
            }
          } catch (err) {
            console.warn('Could not re-fetch dataset — falling back to response body:', err)
          }
        }
        if (dataset) {
          this.$store.dispatch('launchDataset', { dataset })
        }
      } catch (err) {
        console.warn('Could not auto-launch analysis dataset:', err)
      }
    },
    onAnalysisError (err) {
      this.$store.commit('setAlert', { active: false })
      this.$store.commit('setAlert', {
        active: true,
        message: `❌ Analysis failed: ${err?.message || 'unknown error'}`,
        level: 'alert-danger',
        timeout: 10000
      })
    },

    async dismissSaveProgress () {
      const hadCreated = this.saveProgress.items.some(it => it.status === 'done')
      const firstCreated = this.saveProgress.items.find(it => it.status === 'done' && it.result)

      this.saveProgress.active = false
      this.saveProgress.items = []
      this.saveProgress.total = 0
      this.saveProgress.done = 0
      this.saveProgress.failed = 0
      this.saveProgress.percent = 0

      if (hadCreated && firstCreated && firstCreated.result) {
        try {
          this.clearSatelliteLayer()
          this.currentLoadedScene = null
          this.satelliteLayerAdded = false
          this.closeSceneModal()
          this.closeDatasetModal()
          this.$store.commit('hideDrawer', 'satellite')
          // Re-fetch from Firestore to hydrate serverTimestamp() fields as
          // proper Timestamp objects with .toDate() — otherwise DatasetDetails
          // explodes on the "info" tab until a page refresh resyncs the doc.
          let dataset = firstCreated.result
          const datasetId = dataset && (dataset.id || dataset.datasetId)
          if (datasetId) {
            try {
              const fb = require('@/firebaseConfig.js')
              const snap = await fb.datasetsCollection.doc(datasetId).get()
              if (snap.exists) {
                dataset = snap.data()
                dataset.id = snap.id
              }
            } catch (err) {
              console.warn('Could not re-fetch dataset — falling back to response body:', err)
            }
          }
          this.$store.dispatch('launchDataset', { dataset })
        } catch (err) {
          console.warn('Could not auto-launch new dataset:', err)
        }
      }
    },

    async createMultipleDatasets (datasetConfig) {
      // Hide the dataset config modal so the farmer sees the progress overlay
      this.closeDatasetModal()

      // Indices that the single_scene vector recipe supports. Anything in
      // this list, when saveAsRaster is false, is routed through
      // runAnalysisRecipe → vector fishnet output. Everything else (true
      // color, native products, custom expressions, NDWI/MSI/NDSI/GNDVI which
      // we haven't ported yet) stays on the raster path.
      const VECTOR_INDEX_TYPES = new Set([
        // Optical (Sentinel-2 / Landsat / HLS)
        'ndvi', 'ndre', 'savi', 'evi', 'gndvi', 'ndwi', 'msi', 'ndsi',
        // SAR (Sentinel-1 RTC)
        'vv_db', 'vh_db', 'rvi', 'cr'
      ])
      // Frontend type → backend INDEX_CATALOGUE key (mostly identity).
      const VECTOR_INDEX_REMAP = { cr: 'vv_vh_ratio' }

      const useRaster = !!datasetConfig.saveAsRaster
      // Save mode is set by DatasetPreviewModal. Only meaningful when
      // multiple blocks are selected; the modal hides the toggle
      // otherwise so 'combined' is a harmless default.
      const saveMode = datasetConfig.saveMode || 'combined'

      const blocks = this.effectiveBlocks
      if (!blocks.length) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No blocks selected — nothing to save.',
          level: 'alert-warning',
          timeout: 4000
        })
        return
      }
      const multi = blocks.length > 1

      // Build the work-item list. Three patterns possible:
      //
      //   combined + vector  → one item per output, combinedBlockIds
      //                        carries every selected block. The
      //                        single_scene server recipe unions
      //                        geometries and writes ONE dataset per
      //                        output. N datasets total.
      //
      //   combined + raster  → fan out per block. The legacy
      //                        processSatelliteDataset endpoint clips
      //                        to a single block bbox; real combined
      //                        raster would need a separate recipe
      //                        path. For now degrade to per-block in
      //                        the raster case rather than silently
      //                        save only the primary block.
      //
      //   perBlock           → N × M items, one per (block, output).
      //                        Each is a normal single-block run.
      const combinedMode = multi && saveMode === 'combined'
      const items = []
      const isVectorOutput = (type) => VECTOR_INDEX_TYPES.has(type)
      datasetConfig.datasets.forEach(d => {
        const goCombined = combinedMode && !useRaster && isVectorOutput(d.type)
        if (goCombined) {
          // One item, server-side union
          const primary = blocks[0]
          items.push({
            block: primary,
            combinedBlockIds: blocks.map(b => b.id),
            config: d,
            displayName: `${d.name} — combined (${blocks.length} blocks)`,
            type: d.type
          })
        } else if (multi) {
          // Per-block fan-out
          blocks.forEach(block => {
            items.push({
              block,
              combinedBlockIds: null,
              config: d,
              displayName: `${d.name} — ${block.name || block.id}`,
              type: d.type
            })
          })
        } else {
          // Single-block — same as it ever was
          items.push({
            block: blocks[0],
            combinedBlockIds: null,
            config: d,
            displayName: d.name,
            type: d.type
          })
        }
      })

      // Initialize progress state — one item per (block, output) pair.
      this.saveProgress = {
        active: true,
        items: items.map(it => ({
          name: it.displayName,
          type: it.type,
          status: 'pending',
          error: null,
          result: null
        })),
        total: items.length,
        done: 0,
        failed: 0,
        percent: 0
      }

      const sceneCollection = this.currentLoadedScene && (
        this.currentLoadedScene.collection ||
        this.currentLoadedScene.collectionId ||
        (this.currentLoadedScene.properties && this.currentLoadedScene.properties.collection)
      )
      const sceneId = this.currentLoadedScene && (this.currentLoadedScene.id || this.currentLoadedScene.sceneId)

      for (let i = 0; i < items.length; i++) {
        const it = items[i]
        const d = it.config
        const block = it.block
        this.saveProgress.items[i].status = 'running'

        const goVector = !useRaster && VECTOR_INDEX_TYPES.has(d.type) && sceneId && sceneCollection
        try {
          let result
          if (goVector) {
            const indexKey = VECTOR_INDEX_REMAP[d.type] || d.type
            const jobId = `single_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
            const body = {
              recipeId: 'single_scene',
              jobId,
              blockId: block.id,
              farmId: this.$store.state.farm.id,
              userId: this.$store.state.currentUser.uid,
              datasetName: it.displayName,
              datasetDescription: d.description,
              folderId: datasetConfig.folderId || null,
              inputs: {
                sceneId,
                collection: sceneCollection,
                indices: [indexKey],
                includeRasterPreview: true,
                includeRasterDownload: false,
                // In combined mode the server unions every block's
                // footprint into one MultiPolygon, generates the fishnet
                // over the union bbox, and filters cells to only those
                // intersecting some block.
                ...(it.combinedBlockIds ? { combinedBlockIds: it.combinedBlockIds } : {})
              }
            }
            // runAnalysisRecipe requires a verified Firebase ID token —
            // see functions/authMiddleware.js. The server uses the token
            // uid as the authoritative userId for billing + createdBy.
            // eslint-disable-next-line no-await-in-loop
            const idToken = await fb.auth.currentUser.getIdToken()
            // eslint-disable-next-line no-await-in-loop
            const resp = await fetch('https://us-central1-efficient-vineyard.cloudfunctions.net/runAnalysisRecipe', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify(body)
            })
            if (!resp.ok) {
              // eslint-disable-next-line no-await-in-loop
              const text = await resp.text()
              throw new Error(`HTTP ${resp.status}: ${text}`)
            }
            // eslint-disable-next-line no-await-in-loop
            result = await resp.json()
            this.saveProgress.items[i].result = (result.datasets && result.datasets[0] && result.datasets[0].dataset) || result.dataset || null
          } else {
            // Raster path (legacy / opt-in / non-vectorizable types).
            const body = {
              scene: this.currentLoadedScene,
              block,
              farmId: this.$store.state.farm.id,
              userId: this.$store.state.currentUser.uid,
              datasetName: it.displayName,
              datasetDescription: d.description,
              folderId: datasetConfig.folderId || null,
              processingOptions: {
                type: d.type,
                expression: d.expression,
                bands: d.bands,
                colormap: d.colormap,
                useTiTiler: true
              }
            }
            // processSatelliteDataset requires a verified ID token too.
            // eslint-disable-next-line no-await-in-loop
            const idTokenRaster = await fb.auth.currentUser.getIdToken()
            // eslint-disable-next-line no-await-in-loop
            const resp = await fetch('https://us-central1-efficient-vineyard.cloudfunctions.net/processSatelliteDataset', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idTokenRaster}`
              },
              body: JSON.stringify(body)
            })
            if (!resp.ok) {
              // eslint-disable-next-line no-await-in-loop
              const text = await resp.text()
              throw new Error(`HTTP ${resp.status}: ${text}`)
            }
            // eslint-disable-next-line no-await-in-loop
            result = await resp.json()
            this.saveProgress.items[i].result = result.dataset || null
          }
          this.saveProgress.items[i].status = 'done'
          this.saveProgress.done += 1
        } catch (err) {
          console.error(`Error creating ${d.name}:`, err)
          this.saveProgress.items[i].status = 'failed'
          this.saveProgress.items[i].error = err.message
          this.saveProgress.failed += 1
          this.saveProgress.done += 1
        }
        this.saveProgress.percent = Math.round((this.saveProgress.done / this.saveProgress.total) * 100)
      }
      // Progress overlay stays visible so the farmer can review results;
      // clicking "Done" on the overlay clears it and auto-launches the first successful dataset.
    }
  }
}
</script>

<style scoped>
#satellitePanel .selected-block {
  background: #f7f9fc;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}
#satellitePanel .sat-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  padding: 3px;
  background: #f1f3f5;
  border-radius: 8px;
}
#satellitePanel .sat-tab {
  flex: 1;
  padding: 7px 10px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
#satellitePanel .sat-tab:hover { color: #0b5ed7; }
#satellitePanel .sat-tab.is-active {
  background: #fff;
  color: #0b5ed7;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  font-weight: 600;
}
#satellitePanel .loaded-scene {
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  padding: 0.75rem;
}
#satellitePanel .loaded-scene-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
#satellitePanel .scene-icon { font-size: 1.5rem; }
#satellitePanel .tile-loading-inline {
  color: #0b5ed7;
  font-size: 1.1rem;
  line-height: 1;
}

/* Map-overlay spinner — centered over the visible map area (drawer is ~500px wide on left) */
.satellite-map-spinner {
  position: fixed;
  top: 50%;
  left: calc(50% + 250px); /* bias right of drawer */
  transform: translate(-50%, -50%);
  background: rgba(11, 94, 215, 0.92);
  color: #fff;
  padding: 14px 22px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 6px 20px rgba(0,0,0,0.35);
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.02em;
}
.satellite-map-spinner i { font-size: 1.1rem; }
@media(max-width: 900px) {
  .satellite-map-spinner {
    left: 50%;
    top: 100px;
  }
}

/* Full-screen progress overlay for multi-dataset save */
.save-progress-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.save-progress-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.35);
  width: 100%;
  max-width: 520px;
  padding: 22px 24px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}
.save-progress-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
}
.save-progress-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 8px;
}
.save-progress-title i { color: #0b5ed7; }
.save-progress-sub { font-size: 0.85rem; color: #6c757d; }
.save-progress-bar-outer {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 14px;
}
.save-progress-bar-inner {
  height: 100%;
  background: linear-gradient(90deg, #0b5ed7, #3a8ef7);
  border-radius: 10px;
  transition: width 0.3s ease;
}
.save-progress-list {
  overflow-y: auto;
  flex: 1;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fafbfc;
  padding: 6px;
}
.save-progress-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  font-size: 0.88rem;
  border-radius: 4px;
  color: #495057;
}
.save-progress-item + .save-progress-item { border-top: 1px solid #eef1f4; }
.save-progress-item.is-running { background: #f1f8ff; color: #0b5ed7; }
.save-progress-item.is-done .save-progress-icon { color: #198754; }
.save-progress-item.is-failed { background: #fff5f5; color: #842029; }
.save-progress-item.is-failed .save-progress-icon { color: #dc3545; }
.save-progress-icon { width: 18px; text-align: center; flex-shrink: 0; }
.save-progress-name { flex: 1; word-break: break-word; }
.save-progress-error { color: #dc3545; cursor: help; }
.save-progress-footer { margin-top: 14px; }
#satellitePanel .nasa-acres-logo-img {
  max-height: 42px;
  opacity: 0.85;
}
#satellitePanel .nasa-acres-caption {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 4px;
}
#satellitePanel .sponsor-footer a { text-decoration: none; }

#satellitePanel .block-list-section {
  border-top: 1px solid #e5e9f0;
  padding-top: 0.75rem;
}
#satellitePanel .block-list-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: #495057;
  margin-bottom: 0.5rem;
}
#satellitePanel .block-count {
  margin-left: auto;
  font-size: 0.7rem;
  background: #f1f3f5;
  color: #6c757d;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}
#satellitePanel .block-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fafbfc;
}
#satellitePanel .block-list-item {
  display: block;
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid #eef1f4;
  color: #212529;
  text-decoration: none;
  font-size: 0.88rem;
  transition: background 0.1s;
}
#satellitePanel .block-list-item:last-child { border-bottom: none; }
#satellitePanel .block-list-item:hover {
  background: #e9f3fe;
  color: #0b5ed7;
}
#satellitePanel .block-list-item.is-active {
  background: #e9f3fe;
  border-left: 3px solid #0b5ed7;
  padding-left: calc(0.75rem - 3px);
}
#satellitePanel .block-list-name { line-height: 1.2; }

/* Multi-select UI — toggle pill in the header, bulk-action row, and
   per-row checkbox indicator. Visual language matches the rest of the
   satellite drawer (#0b5ed7 accent, light backgrounds). */
#satellitePanel .multi-toggle {
  border: 1px solid #ced4da;
  background: #fff;
  border-radius: 12px;
  font-size: 0.72rem;
  padding: 1px 8px;
  color: #495057;
  cursor: pointer;
  margin-left: auto;
  transition: all 0.12s;
}
#satellitePanel .multi-toggle:hover { border-color: #0b5ed7; color: #0b5ed7; }
#satellitePanel .multi-toggle.is-on {
  background: #0b5ed7;
  border-color: #0b5ed7;
  color: #fff;
}
#satellitePanel .multi-toggle i { margin-right: 3px; }
#satellitePanel .multi-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 4px 4px;
  font-size: 0.78rem;
}
#satellitePanel .multi-actions .btn-link {
  background: none;
  border: none;
  color: #0b5ed7;
  cursor: pointer;
  padding: 0;
  font-size: 0.78rem;
}
#satellitePanel .multi-actions .btn-link:disabled {
  color: #adb5bd;
  cursor: not-allowed;
}
#satellitePanel .multi-actions .btn-link:hover:not(:disabled) {
  text-decoration: underline;
}
#satellitePanel .multi-actions .multi-summary {
  margin-left: auto;
  color: #6c757d;
}
#satellitePanel .block-list-item.is-extra {
  background: #e7f1ff;
}
#satellitePanel .multi-check { color: #0b5ed7; }
</style>
